import React, { useState, useEffect } from "react";
import {
	X,
	Folder,
	File,
	Download,
	ChevronRight,
	Home,
	Loader,
	AlertCircle,
	FolderOpen,
} from "lucide-react";
import { apiClient } from "../../services/api";

const BOMHistoryModal = ({ isOpen, onClose }) => {
	const [currentPath, setCurrentPath] = useState("");
	const [folders, setFolders] = useState([]);
	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [pathHistory, setPathHistory] = useState([]);
	const [downloadingItem, setDownloadingItem] = useState(null);
	const [folderInfo, setFolderInfo] = useState(null);

	// Load folder contents
	const loadFolderContents = async (path = "") => {
		setLoading(true);
		setError(null);
		try {
			const response = await apiClient.get("/s3-browser/list", {
				params: { prefix: path },
			});
			setFolders(response.data.folders || []);
			setFiles(response.data.files || []);
			setCurrentPath(response.data.currentPath || "");

			// Load folder info
			loadFolderInfo(path);
		} catch (err) {
			console.error("Error loading folder contents:", err);
			setError("Failed to load folder contents. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	// Load folder info (file count and size)
	const loadFolderInfo = async (path = "") => {
		try {
			const response = await apiClient.get("/s3-browser/info", {
				params: { path },
			});
			setFolderInfo(response.data);
		} catch (err) {
			console.error("Error loading folder info:", err);
		}
	};

	// Initial load
	useEffect(() => {
		if (isOpen) {
			loadFolderContents("");
			setPathHistory([]);
		}
	}, [isOpen]);

	// Navigate into folder
	const handleFolderClick = (folder) => {
		setPathHistory([...pathHistory, currentPath]);
		loadFolderContents(folder.path);
	};

	// Navigate back
	const handleBackClick = () => {
		if (pathHistory.length > 0) {
			const previousPath = pathHistory[pathHistory.length - 1];
			setPathHistory(pathHistory.slice(0, -1));
			loadFolderContents(previousPath);
		}
	};

	// Navigate to root
	const handleHomeClick = () => {
		setPathHistory([]);
		loadFolderContents("");
	};

	// Download file or folder
	const handleDownload = async (item) => {
		setDownloadingItem(item.path);
		try {
			const response = await apiClient.get("/s3-browser/download", {
				params: { path: item.path },
				responseType: "blob",
			});

			// Create download link
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement("a");
			link.href = url;

			// Determine filename
			const filename = item.type === "folder" ? `${item.name}.zip` : item.name;

			link.setAttribute("download", filename);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			console.error("Error downloading:", err);
			alert("Failed to download. Please try again.");
		} finally {
			setDownloadingItem(null);
		}
	};

	// Format file size
	const formatSize = (bytes) => {
		if (!bytes) return "-";
		const sizes = ["B", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(1024));
		return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
	};

	// Format date
	const formatDate = (dateString) => {
		if (!dateString) return "-";
		const date = new Date(dateString);
		return date.toLocaleDateString() + " " + date.toLocaleTimeString();
	};

	if (!isOpen) return null;

	return (
		<div style={styles.overlay}>
			<div style={styles.modal}>
				{/* Header */}
				<div style={styles.header}>
					<div style={styles.headerLeft}>
						<FolderOpen size={24} style={{ marginRight: "10px" }} />
						<h2 style={styles.title}>BOM History</h2>
					</div>
					<button onClick={onClose} style={styles.closeButton}>
						<X size={24} />
					</button>
				</div>

				{/* Breadcrumb and Info */}
				<div style={styles.breadcrumbContainer}>
					<div style={styles.breadcrumb}>
						<button
							onClick={handleHomeClick}
							style={styles.breadcrumbButton}
							disabled={currentPath === ""}
						>
							<Home size={16} />
						</button>
						{pathHistory.length > 0 && (
							<button onClick={handleBackClick} style={styles.breadcrumbButton}>
								← Back
							</button>
						)}
						<span style={styles.breadcrumbPath}>/vinlist/{currentPath}</span>
					</div>
					{folderInfo && (
						<div style={styles.folderInfo}>
							<span>{folderInfo.fileCount} files</span>
							<span style={{ marginLeft: "15px" }}>
								{folderInfo.formattedSize}
							</span>
						</div>
					)}
				</div>

				{/* Content */}
				<div style={styles.content}>
					{loading ? (
						<div style={styles.loadingContainer}>
							<Loader
								size={48}
								style={{ animation: "spin 1s linear infinite" }}
							/>
							<p>Loading...</p>
						</div>
					) : error ? (
						<div style={styles.errorContainer}>
							<AlertCircle size={48} color="#e74c3c" />
							<p style={{ color: "#e74c3c", marginTop: "10px" }}>{error}</p>
							<button
								onClick={() => loadFolderContents(currentPath)}
								style={styles.retryButton}
							>
								Retry
							</button>
						</div>
					) : (
						<table style={styles.table}>
							<thead>
								<tr>
									<th style={styles.tableHeader}>Name</th>
									<th
										style={{
											...styles.tableHeader,
											textAlign: "right",
											paddingRight: "40px",
										}}
									>
										Actions
									</th>
								</tr>
							</thead>
							<tbody>
								{folders.map((folder) => (
									<tr key={folder.path} style={styles.tableRow}>
										<td style={styles.tableCell}>
											<button
												onClick={() => handleFolderClick(folder)}
												style={styles.folderButton}
											>
												<Folder
													size={20}
													color="#3498db"
													style={{ marginRight: "8px" }}
												/>
												{folder.name}
												<ChevronRight
													size={16}
													style={{ marginLeft: "100px" }}
												/>
											</button>
										</td>
										<td
											style={{
												...styles.tableCell,
												textAlign: "right",
												paddingRight: "40px",
											}}
										>
											<div
												style={{
													display: "flex",
													flexDirection: "column",
													alignItems: "flex-end",
													gap: "4px",
												}}
											>
												<button
													onClick={() => handleDownload(folder)}
													disabled={downloadingItem === folder.path}
													style={styles.downloadButton}
													title="Download folder as ZIP"
												>
													{downloadingItem === folder.path ? (
														<Loader
															size={16}
															style={{ animation: "spin 1s linear infinite" }}
														/>
													) : (
														<Download size={16} />
													)}
												</button>
											</div>
										</td>
									</tr>
								))}
								{files.map((file) => (
									<tr key={file.path} style={styles.tableRow}>
										<td style={styles.tableCell}>
											<div style={styles.fileNameContainer}>
												<File
													size={20}
													color="#95a5a6"
													style={{ marginRight: "8px" }}
												/>
												{file.name}
											</div>
										</td>
										<td
											style={{
												...styles.tableCell,
												textAlign: "right",
												paddingRight: "40px",
											}}
										>
											<div
												style={{
													display: "flex",
													flexDirection: "column",
													alignItems: "flex-end",
													gap: "4px",
												}}
											>
												<button
													onClick={() => handleDownload(file)}
													disabled={downloadingItem === file.path}
													style={styles.downloadButton}
													title="Download file"
												>
													{downloadingItem === file.path ? (
														<Loader
															size={16}
															style={{ animation: "spin 1s linear infinite" }}
														/>
													) : (
														<Download size={16} />
													)}
												</button>
												<span style={{ fontSize: "11px", color: "#6c757d" }}>
													Download
												</span>
											</div>
										</td>
									</tr>
								))}
								{folders.length === 0 && files.length === 0 && (
									<tr>
										<td colSpan={2} style={styles.emptyMessage}>
											This folder is empty
										</td>
									</tr>
								)}
							</tbody>
						</table>
					)}
				</div>

				{/* Footer */}
				<div style={styles.footer}>
					<p style={styles.footerText}>
						Browse and download files from the vinlist folder. Folders will be
						downloaded as ZIP files.
					</p>
				</div>
			</div>

			<style>
				{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
			</style>
		</div>
	);
};

const styles = {
	overlay: {
		position: "fixed",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0, 0, 0, 0.6)",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		zIndex: 10000,
		padding: "20px",
	},
	modal: {
		backgroundColor: "white",
		borderRadius: "8px",
		width: "80%",
		maxWidth: "900px",
		maxHeight: "80vh",
		display: "flex",
		flexDirection: "column",
		boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
		fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
	},
	header: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: "20px",
		borderBottom: "2px solid #e0e0e0",
		backgroundColor: "#f8f9fa",
	},
	headerLeft: {
		display: "flex",
		alignItems: "center",
	},
	title: {
		margin: 0,
		fontSize: "20px",
		fontWeight: "bold",
		fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
	},
	closeButton: {
		background: "none",
		border: "none",
		cursor: "pointer",
		padding: "5px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		borderRadius: "4px",
		transition: "background-color 0.2s",
	},
	breadcrumbContainer: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: "15px 20px",
		backgroundColor: "#f8f9fa",
		borderBottom: "1px solid #e0e0e0",
	},
	breadcrumb: {
		display: "flex",
		alignItems: "center",
		gap: "10px",
		fontSize: "14px",
	},
	breadcrumbButton: {
		padding: "5px 10px",
		backgroundColor: "#e9ecef",
		border: "1px solid #dee2e6",
		borderRadius: "4px",
		cursor: "pointer",
		fontSize: "14px",
		display: "flex",
		alignItems: "center",
		gap: "5px",
		transition: "all 0.2s",
	},
	breadcrumbPath: {
		fontFamily: "monospace",
		color: "#495057",
		fontSize: "14px",
	},
	folderInfo: {
		fontSize: "13px",
		color: "#6c757d",
	},
	content: {
		flex: 1,
		overflow: "auto",
		padding: "0",
	},
	loadingContainer: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		height: "400px",
		color: "#6c757d",
	},
	errorContainer: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		height: "400px",
	},
	retryButton: {
		marginTop: "15px",
		padding: "8px 16px",
		backgroundColor: "#3498db",
		color: "white",
		border: "none",
		borderRadius: "4px",
		cursor: "pointer",
		fontSize: "14px",
	},
	table: {
		width: "100%",
		borderCollapse: "collapse",
	},
	tableHeader: {
		padding: "12px 20px",
		textAlign: "left",
		backgroundColor: "#f8f9fa",
		borderBottom: "2px solid #dee2e6",
		fontWeight: "bold",
		fontSize: "14px",
		color: "#495057",
		fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
	},
	tableRow: {
		borderBottom: "1px solid #e9ecef",
		transition: "background-color 0.2s",
	},
	tableCell: {
		padding: "12px 20px",
		fontSize: "14px",
		color: "#212529",
	},
	folderButton: {
		display: "flex",
		alignItems: "center",
		width: "100%",
		padding: "5px",
		background: "none",
		border: "none",
		cursor: "pointer",
		fontSize: "14px",
		color: "#212529",
		textAlign: "left",
		borderRadius: "4px",
		transition: "background-color 0.2s",
	},
	fileNameContainer: {
		display: "flex",
		alignItems: "center",
	},
	downloadButton: {
		padding: "6px 12px",
		backgroundColor: "#28a745",
		color: "white",
		border: "none",
		borderRadius: "4px",
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		transition: "background-color 0.2s",
		minWidth: "40px",
	},
	emptyMessage: {
		padding: "40px",
		textAlign: "center",
		color: "#6c757d",
		fontStyle: "italic",
	},
	footer: {
		padding: "15px 20px",
		borderTop: "1px solid #e0e0e0",
		backgroundColor: "#f8f9fa",
	},
	footerText: {
		margin: 0,
		fontSize: "13px",
		color: "#6c757d",
		textAlign: "center",
	},
};

export default BOMHistoryModal;
